import { usePrevious } from '@sequencehq/utils'
import { useEffect, useRef } from 'react'
import { useLogEvent } from 'lib/postHog/useLogEvent'

/**
 * Logs to PostHog how long a query takes to resolve, measured from when its
 * fetch starts (i.e. when the query inputs change) to when it next settles
 * successfully. Use it to spot a slow query within a multi-query flow — and,
 * as a side effect, to track how often that query actually runs.
 *
 * Cache hits aren't logged: they don't fetch, so there is no resolution time
 * to measure.
 */
export const useLogQueryResolutionTime = (params: {
  eventName: string
  isFetching: boolean
  isSuccess: boolean
  metadata?: Record<string, unknown>
}) => {
  const { logEvent } = useLogEvent()
  const fetchStartedAt = useRef<number | null>(null)
  const wasFetching = usePrevious(params.isFetching)

  useEffect(() => {
    const fetchStarted = params.isFetching && !wasFetching
    const fetchSettled = !params.isFetching && wasFetching

    if (fetchStarted) {
      fetchStartedAt.current = performance.now()
      return
    }

    const startedAt = fetchStartedAt.current

    if (fetchSettled && startedAt !== null) {
      if (params.isSuccess) {
        logEvent({
          eventName: params.eventName,
          metadata: {
            resolutionTimeMs: Math.round(performance.now() - startedAt),
            ...(params.metadata ?? {})
          }
        })
      }
      fetchStartedAt.current = null
    }
  }, [
    params.isFetching,
    params.isSuccess,
    params.eventName,
    params.metadata,
    wasFetching,
    logEvent
  ])
}
